/* eslint-disable no-restricted-syntax */
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import NavigationPrompt from 'react-router-navigation-prompt';
import { ArrayHelpers, FormikProps } from 'formik';
import { Alert } from 'components';
import { FetchDataQueryType } from 'providers/ListContextProvider';
import routeConstants, { URL } from 'routes/routeConstants';
import { browserHistory } from 'routes/Router';
import { getPayeesRequest } from 'store/Payment';
import { ICurrentEmployee, IEmployerState } from 'store/types';
import { IEmployee, IEmployerSite, IHoldbackFixed, IHoldbackPercent, IPayee, ISiteRolePolicy, IUploadPayment, PayoutMethodTypes } from 'types';
import { convertEmployeeToPayee, isEmployeePayable } from 'utils';
import { MakePaymentStep } from './MakePaymentTypes';
import PaymentInput from './PaymentInput';
import PaymentReview from './PaymentReview';
import PaymentSummary from './PaymentSummary';
import SelectPayees from './SelectPayees';
import SelectSite from './SelectSite';
import UploadPayment from './UploadPayment';

interface IMakeContentProps {
  payees: IPayee[];
  // these are convenience methods provided by Formik to help with dynamically-generated form fields. Simply deleting/inserting/editing form fields within an array will mess up their associated form metadata if done outside of these helpers
  arrayHelpers: ArrayHelpers;
  step: MakePaymentStep;
  currentEmployee: ICurrentEmployee;
  currentSite?: IEmployerSite | null;
  currentSiteId: string;
  formik: FormikProps<{
    payees: IPayee[];
    description: string;
    authorizedAgreement: boolean;
    businessDate: string;
  }>;
  siteBalance: number;
  setStep: (step: MakePaymentStep) => void;
  setMSidebarExpanded: (expended: boolean) => void;
  mSidebarExpanded: boolean;
  employees: IEmployee[];
  currentEmployeeSites: IEmployerSite[];
  pageHeaderContent: React.ReactNode;
  currentOrganization: IEmployerState;
  currentOrganizationId: string;
  onAddToPayeesList: (id: string) => void;
  onAddUploadPaymentToPayeesList: (employee: IEmployee, tipAmount: number) => void;
  onRemovePayee: (index: number) => void;
  onRemoveAllPayees: () => void;
  totalTips: number;
  totalInstantTips: number;
  totalPayrollTips: number;
  tipPoolLimit?: number;
  setTipPoolLimit: (tip: number | undefined) => void;
  perTipMax?: number | '';
  siteUsesHoldbacks: boolean;
  siteHoldbacks?: IHoldbackFixed | IHoldbackPercent;
  totalHoldbacks?: number | '';
  currentUserName: string;
  clearPayeesList: () => void;
  isProcessing: boolean;
  showOverdraftWarning: boolean;
  overdraftText: React.ReactNode;
  overdraftContinue: boolean;
  onSelectSite: (site: IEmployerSite) => void;
  onPayoutMethodSelect: (method: PayoutMethodTypes) => void;
  fetchData: (query: FetchDataQueryType) => void;
  rolePolicy?: ISiteRolePolicy;
  strictTipPool: boolean;
  createPaymentsEnabled?: boolean;
  paymentCategories?: string[];
}

export const MakePaymentContent = ({
  payees,
  arrayHelpers,
  step,
  currentEmployee,
  currentSite,
  currentSiteId,
  formik,
  siteBalance,
  setStep,
  setMSidebarExpanded,
  mSidebarExpanded,
  employees,
  currentEmployeeSites,
  currentOrganization,
  currentOrganizationId,
  pageHeaderContent,
  onAddToPayeesList,
  onAddUploadPaymentToPayeesList,
  onRemovePayee,
  onRemoveAllPayees,
  totalTips,
  totalInstantTips,
  totalPayrollTips,
  tipPoolLimit,
  setTipPoolLimit,
  perTipMax,
  siteUsesHoldbacks,
  totalHoldbacks,
  currentUserName,
  clearPayeesList,
  isProcessing,
  showOverdraftWarning,
  overdraftText,
  overdraftContinue,
  onSelectSite,
  onPayoutMethodSelect,
  fetchData,
  siteHoldbacks,
  rolePolicy,
  createPaymentsEnabled,
  paymentCategories,
  strictTipPool = false,
}: IMakeContentProps) => {
  const dispatch = useDispatch();
  const [allPayeesList, setAllPayeesList] = useState<IPayee[]>();
  const [tipPoolEnabled, setTipPoolEnabled] = useState<boolean>(false);
  const [paymentUploads, setPaymentUploads] = useState<IUploadPayment[]>([]);
  const isUploadingPayment = paymentUploads.length > 0;

  useEffect(() => {
    if (allPayeesList && rolePolicy) {
      for (const payee of allPayeesList) {
        // filter employees that were already added or are not eligible for tips
        if (!payees.some((py) => py.id === payee.id) && isEmployeePayable(payee, rolePolicy, currentEmployee)) {
          arrayHelpers.push(payee);
        }
      }
    }
  }, [allPayeesList]);

  const onAddAllEmployeesToPayeesList = (query: FetchDataQueryType) => {
    if (currentSite) {
      const { sort, meta, q } = query;
      const currentSiteId = meta as string;
      const nonStatusQuery = (q || []).filter((q) => q.key !== 'status');
      dispatch(getPayeesRequest(currentSiteId, { sort, limit: 0, q: nonStatusQuery }, siteHoldbacks, setAllPayeesList));
    }
  };

  const onAddForgottenEmployeeSelect = (employeeToAdd: IEmployee) => {
    // add the selected user
    if (employeeToAdd) {
      arrayHelpers.push(convertEmployeeToPayee(employeeToAdd, currentSiteId, siteHoldbacks));

      // wait for the arrayHelpers to update then validate the form
      setTimeout(() => {
        if (formik.errors.payees) formik.validateForm();
      }, 1000);
    }
  };

  const isEmployeeDisabled = (employee: IEmployee, upload?: boolean) => {
    const alreadyAddedMsg = 'Employee has already been added';
    if (upload) {
      if (paymentUploads.some((payment) => payment.employee?.id === employee.id)) {
        return alreadyAddedMsg;
      }
    } else if (payees.some((py) => py.id === employee.id)) {
      return alreadyAddedMsg;
    }
    if (rolePolicy && !isEmployeePayable(employee, rolePolicy, currentEmployee)) {
      return 'You are not allowed to issue tips to this user.';
    }
    return '';
  };

  return (
    <>
      <NavigationPrompt
        when={(crntLocation, nextLocation) => {
          // when redirected after a successful submission
          if (nextLocation?.pathname === URL.MakePaymentSummary) return false;

          // when redirected after logout or changing organization
          if (nextLocation?.pathname === URL.Login || nextLocation?.pathname === URL.RootPrivate) return false;

          return (!nextLocation || !nextLocation.pathname.startsWith(crntLocation.pathname)) && (payees?.length > 0 || isUploadingPayment);
        }}
      >
        {({ onConfirm, onCancel }) => (
          <Alert
            data-cy="leave-payment-alert"
            title="Leave make payment?"
            message="Leaving this page will abandon all work. Are you sure you want to leave?"
            onLeftAction={onCancel}
            onRightAction={() => {
              setTipPoolEnabled(false);
              setTipPoolLimit(undefined);
              clearPayeesList();
              setPaymentUploads([]);
              onConfirm();
            }}
            leftActionLabel="Cancel"
            rightActionLabel="Leave"
            isActive
          />
        )}
      </NavigationPrompt>

      {step === MakePaymentStep.SelectSite && <SelectSite onPayoutMethodSelect={onPayoutMethodSelect} onSelectSite={onSelectSite} currentEmployeeSites={currentEmployeeSites} currentSite={currentSite} />}

      {currentSite && rolePolicy && step === MakePaymentStep.SelectPayees && (
        <SelectPayees
          fetchData={fetchData}
          balance={siteBalance}
          onBack={() => {
            browserHistory.push(routeConstants.private.makePayment);
          }}
          onNext={() => {
            formik.validateForm();
            setStep(MakePaymentStep.PaymentInput);
          }}
          onExpand={() => setMSidebarExpanded(!mSidebarExpanded)}
          sidebarOpen={mSidebarExpanded}
          payees={formik.values.payees}
          employees={employees}
          currentEmployee={currentEmployee}
          currentEmployeeSites={currentEmployeeSites}
          currentSite={currentSite}
          currentSiteId={currentSiteId}
          pageHeaderContent={pageHeaderContent}
          currentOrganization={currentOrganization}
          currentOrganizationId={currentOrganizationId}
          onAddToPayeesList={onAddToPayeesList}
          onAddAllEmployeesToPayeesList={onAddAllEmployeesToPayeesList}
          onRemovePayee={onRemovePayee}
          rolePolicy={rolePolicy}
        />
      )}

      {currentSite && step === MakePaymentStep.UploadPayment && (
        <UploadPayment
          currentEmployee={currentEmployee}
          currentSiteId={currentSiteId}
          pageHeaderContent={pageHeaderContent}
          onAddUploadPaymentToPayeesList={onAddUploadPaymentToPayeesList}
          onBack={() => {
            browserHistory.push(routeConstants.private.makePayment);
          }}
          onNext={() => {
            setStep(MakePaymentStep.PaymentInput);
          }}
          onRemoveAllPayees={onRemoveAllPayees}
          paymentUploads={paymentUploads}
          setPaymentUploads={setPaymentUploads}
          sidebarOpen={mSidebarExpanded}
          onExpand={() => setMSidebarExpanded(!mSidebarExpanded)}
          isEmployeeDisabled={(emp) => isEmployeeDisabled(emp, true)}
          tipPoolEnabled={tipPoolEnabled}
          tipPoolLimit={tipPoolLimit}
          rolePolicy={rolePolicy}
        />
      )}

      {currentSite && siteHoldbacks && perTipMax && step === MakePaymentStep.PaymentInput && (
        <PaymentInput
          balance={siteBalance}
          onBack={() => (isUploadingPayment ? setStep(MakePaymentStep.UploadPayment) : setStep(MakePaymentStep.SelectPayees))}
          onNext={() => setStep(MakePaymentStep.PaymentReview)}
          onExpand={() => setMSidebarExpanded(!mSidebarExpanded)}
          onRemovePayee={onRemovePayee}
          sidebarOpen={mSidebarExpanded}
          currentEmployeeSites={currentEmployeeSites}
          currentSite={currentSite}
          currentSiteId={currentSiteId}
          pageHeaderContent={pageHeaderContent}
          payees={payees}
          isPaymentUpload={isUploadingPayment}
          isEmployeeDisabled={isEmployeeDisabled}
          totalTips={totalTips}
          totalInstantTips={totalInstantTips}
          totalPayrollTips={totalPayrollTips}
          tipPoolLimit={tipPoolLimit}
          setTipPoolLimit={setTipPoolLimit}
          employeeTipLimit={perTipMax}
          siteUsesHoldbacks={siteUsesHoldbacks}
          siteHoldbacks={siteHoldbacks}
          totalHoldbacks={totalHoldbacks || 0}
          rolePolicy={rolePolicy}
          arrayHelpers={arrayHelpers}
          formik={formik}
          onAddForgottenEmployeeSelect={onAddForgottenEmployeeSelect}
          showOverdraftWarning={showOverdraftWarning}
          overdraftText={overdraftText || ''}
          overdraftContinue={overdraftContinue}
          tipPoolEnabled={tipPoolEnabled || strictTipPool}
          setTipPoolEnabled={(enabled: boolean) => {
            setTipPoolEnabled(enabled);
            if (!enabled) setTipPoolLimit(undefined);
          }}
          strictTipPool={strictTipPool}
        />
      )}

      {currentSite && step === MakePaymentStep.PaymentReview && (
        <PaymentReview
          balance={siteBalance}
          createPaymentsEnabled={createPaymentsEnabled}
          description={formik.values.description}
          onBack={() => setStep(MakePaymentStep.PaymentInput)}
          sidebarOpen={mSidebarExpanded}
          currentEmployeeSites={currentEmployeeSites}
          currentSite={currentSite}
          currentSiteId={currentSiteId}
          pageHeaderContent={pageHeaderContent}
          currentOrganization={currentOrganization}
          currentOrganizationId={currentOrganizationId}
          totalTips={totalTips}
          totalInstantTips={totalInstantTips}
          totalPayrollTips={totalPayrollTips}
          tipPoolLimit={tipPoolLimit}
          payees={formik.values.payees}
          valid={formik.isValid}
          setFieldValue={formik.setFieldValue}
          acceptAgreementProps={formik.getFieldProps('authorizedAgreement')}
          paymentCategoryProps={formik.getFieldProps('paymentCategory')}
          paymentCategories={paymentCategories}
          onSubmit={formik.handleSubmit}
          businessDate={formik.values.businessDate}
          setFieldTouched={formik.setFieldTouched}
          businessDateError={formik.errors.businessDate}
          businessDateTouched={formik.touched.businessDate}
          currentUserName={currentUserName}
          isProcessing={isProcessing}
          siteUsesHoldbacks={siteUsesHoldbacks}
          totalHoldbacks={totalHoldbacks || 0}
          showOverdraftWarning={showOverdraftWarning}
          overdraftContinue={overdraftContinue}
          overdraftText={overdraftText || ''}
          formik={formik}
        />
      )}

      {step === MakePaymentStep.PaymentSummary && <PaymentSummary />}
    </>
  );
};
